import { Link } from "react-router-dom";
import type { StoryMeta } from "~/types/story";
import { useBookshelf } from "~/contexts/BookshelfContext";

interface StoryCardProps {
  story: StoryMeta;
}

export default function StoryCard({ story }: StoryCardProps) {
  const { isFavorite, toggleFavorite, isRead } = useBookshelf();

  const categoryColors: Record<string, string> = {
    "historical-figures": "bg-amber-100 text-amber-800",
    "world-history": "bg-rose-100 text-rose-800",
    "science-nature": "bg-emerald-100 text-emerald-800",
    "space-astronomy": "bg-indigo-100 text-indigo-800",
    "inventions-technology": "bg-blue-100 text-blue-800",
    "geography-cultures": "bg-orange-100 text-orange-800",
    "arts-music": "bg-purple-100 text-purple-800",
    "life-skills": "bg-pink-100 text-pink-800",
    "math-logic": "bg-cyan-100 text-cyan-800",
    "health-body": "bg-lime-100 text-lime-800",
  };

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1">
      <Link to={`/age/${story.ageGroup}/${story.slug}`} className="block p-6">
        <div className="flex items-start justify-between mb-3">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${categoryColors[story.category] || "bg-gray-100 text-gray-800"}`}>
            {story.category.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}
          </span>
          <div className="flex items-center gap-2">
            {isRead(story.id) && (
              <span className="text-xs text-green-600 dark:text-green-400" title="Read">&#10003;</span>
            )}
            <span className="text-xs text-gray-400">{story.readingTime} min</span>
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {story.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {story.description}
        </p>

        <div className="flex flex-wrap gap-1">
          {story.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs text-gray-400 dark:text-gray-500">
              #{tag}
            </span>
          ))}
        </div>
      </Link>

      <button
        onClick={(e) => { e.preventDefault(); toggleFavorite(story.id); }}
        className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title={isFavorite(story.id) ? "Remove from bookshelf" : "Add to bookshelf"}
      >
        <svg className="w-5 h-5" fill={isFavorite(story.id) ? "#ef4444" : "none"} stroke={isFavorite(story.id) ? "#ef4444" : "currentColor"} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      </button>
    </div>
  );
}
